import { AnalyticsService } from 'base/analytics';
import { pluralise } from 'base/helpers';
import { getLocalStorageItem, listenToStorageEvent } from 'base/localstorage';
import { generateReferralUrl } from 'base/monotype';
import { TypographyTarget } from 'base/styleguide/model';
import { Badge } from 'base/ui/badge';
import { Button } from 'base/ui/button';
import { Callout, useCalloutState } from 'base/ui/callout';
import { Icon } from 'base/ui/icon';
import {
  IconClear,
  IconDisplay,
  IconGoogle,
  IconMono,
  IconReplaceAi,
  IconSansSerif,
  IconSearch,
  IconSerif,
  IconShuffleAi,
  IconSingleStar,
  IconStars,
  IconThumbsUpFilled,
  IconVisibleOutline,
} from 'base/ui/icons';
import { InfiniteScroll } from 'base/ui/infinite_scroll';
import { FormInput } from 'base/ui/input';
import { Inline, Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import {
  DividerLine,
  ExpandToEdges,
  PanelLayout,
  PanelScrollable,
} from 'base/ui/panel';
import { TabList } from 'base/ui/tabs';
import { ToastQueue } from 'base/ui/toast';
import { Toggle } from 'base/ui/toggle';
import {
  TextRegular,
  TextSmall,
  TextSmallLight,
  TextTiny,
  TextTinyLight,
} from 'base/ui/typography';
import { useOnMount } from 'base/useOnMount';
import { useStableCallback } from 'base/useStableCallback';
import {
  BlocksService,
  FontCollection,
  FontFamilyMetadata,
  FontFamilyPagedResponse,
  ListFontFamiliesFromCollectionsResponse,
  ListRecommendedFontFamiliesResponse,
} from 'blocks/blocks_service';
import { ProjectSession } from 'blocks/editor/session/session';
import { FontMetaInfo } from 'blocks/editor/styleguide/FontMeta';
import classNames from 'classnames';
import { action, makeAutoObservable, observable, reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncyEffect } from 'use-debouncy';
import { ConceptsStore, getGlobalConceptsStore } from './concept_store';
import {
  BODY_FONT_COLLECTION_SLUGS,
  globalFontOverrideStore,
  globalRemoteFontStore,
  HeadingFontCollectionSlug,
  HEADING_FONT_COLLECTIONS,
  HEADING_FONT_COLLECTION_SLUGS,
} from './fonts';
import { SidebarNavigation, TypographyCategory } from './routes';
import typeDisplayImage from './type-display.file.svg';
import typeMonospaceImage from './type-monospace.file.svg';
import typeSansSerifImage from './type-sans-serif.file.svg';
import typeSerifImage from './type-serif.file.svg';
import styles from './typography_panel.css';
import { getIconForFontSource, LikeButton, SegmentedFooter } from './ui';

interface TypographyEditorPanelProps {
  analytics: AnalyticsService;
  blocksService: BlocksService;
  navigation: SidebarNavigation;
  session: ProjectSession;
  toastQueue: ToastQueue;
}

type CommitFontFamily = (fontFamily: FontFamilyMetadata) => void;

const FreeFontFilterToggle = observer(function _FreeFontFilterToggle({
  target,
}: {
  target: TypographyTarget;
}) {
  const globalTypographyPanelStore = getGlobalTypographyPanelStore();
  const includePremiumFonts = globalTypographyPanelStore.includePremiumFonts;
  return (
    <div className={styles.freeFontFilterContainer}>
      <Inline yalign="center" spacing={8}>
        <Icon icon={IconGoogle} size={16} color="colorVeryLightPurple" />
        <TextSmall>Only Free fonts </TextSmall>
      </Inline>
      <Toggle
        value={!includePremiumFonts}
        onChange={() => {
          globalTypographyPanelStore.toggleFilteringPremiumFonts();
          if (!globalTypographyPanelStore.getRecommendedFontFamilies(target)) {
            globalTypographyPanelStore.reloadRecommendedFontFamilies(target);
          }
          if (!globalTypographyPanelStore.getPopularFontFamilies(target)) {
            globalTypographyPanelStore.reloadPopularFontFamilies(target);
          }
        }}
      />
    </div>
  );
});

export const FontFamilyCard = observer(function _FontFamilyCard({
  analytics,
  category,
  collection,
  fontFamily,
  commitFontFamily,
  tagline,
  size = 'large',
  target,
  toastQueue,
  footerText,
  showLikeCallout = false,
}: {
  analytics: AnalyticsService;
  fontFamily: FontFamilyMetadata;
  commitFontFamily?: CommitFontFamily;
  category:
    | 'search'
    | 'popular'
    | 'recommended'
    | 'liked'
    | 'collection'
    | 'onboarding';
  collection?: HeadingFontCollectionSlug | 'body';
  tagline?: string;
  size?: 'small' | 'large';
  target: TypographyTarget;
  toastQueue: ToastQueue;
  footerText?: string;
  showLikeCallout?: boolean;
}) {
  const conceptsStore = getGlobalConceptsStore();
  const concept = conceptsStore.activeConcept;
  const liked = concept.isFontFamilyLiked(target, fontFamily.slug);
  const onLikeChange = (liked: boolean) => {
    setFontFamilyLiked(target, fontFamily, liked, conceptsStore, toastQueue);

    analytics.track({
      event: liked
        ? 'project:like_font_family_button_click'
        : 'project:unlike_font_family_button_click',
      target,
      font_family: fontFamily.slug,
      category,
      collection,
    });
  };

  const isOverridingRef = useRef(false);
  useEffect(
    () => () => {
      if (isOverridingRef.current) {
        globalFontOverrideStore.clearFontOverride(target);
      }
    },
    [target],
  );

  const isSelected = concept.getFontFamily(target).slug === fontFamily.slug;

  const fontWeights = concept.getFontWeights(target);
  const isLoaded = globalRemoteFontStore.isRemoteFontFamilyLoaded(
    fontFamily.slug,
    fontWeights,
  );

  const likeCalloutRef = useRef<HTMLDivElement>(null);
  const { visible: likeCalloutVisible, dismiss: dismissLikeCallout } =
    useCalloutState({ key: 'sgb-like-callout' });

  const footer =
    footerText == null ? (
      <FontMetaInfo
        fontFamily={fontFamily}
        priceDisplayMode={size === 'small' ? 'none' : 'simple'}
      />
    ) : footerText !== '' ? (
      <TextTinyLight color="grey">{footerText}</TextTinyLight>
    ) : null;

  const previewButton = (
    <div className={styles.fontCardPreviewIcon}>
      <Button
        icon={IconVisibleOutline}
        iconColor={isLoaded ? 'black' : 'grey'}
        variant="secondary"
        size="small"
        onMouseEnter={() => {
          globalFontOverrideStore.setFontOverride(target, fontFamily.slug);
          isOverridingRef.current = true;
          analytics.track({
            event: 'project:preview_font_family_button_hover',
            target,
            font_family: fontFamily.slug,
            category,
            collection,
          });
        }}
        onMouseLeave={() => {
          globalFontOverrideStore.clearFontOverride(target);
          isOverridingRef.current = false;
        }}
        onClick={(e) => e.stopPropagation()}
        tooltip={isLoaded ? 'Previewing' : ''}
      />
    </div>
  );

  return (
    <>
      <div
        className={classNames(
          styles.fontCard,
          size === 'small' && styles.fontCardSmall,
          size === 'large' && styles.fontCardLarge,
          !!commitFontFamily && styles.fontCardSelectable,
        )}
        onClick={() => {
          if (!commitFontFamily) return;

          commitFontFamily(fontFamily);
          analytics.track({
            event: 'project:set_font_family_button_click',
            target,
            font_family: fontFamily.slug,
            category,
            collection,
          });
        }}
      >
        <Rows spacing={10}>
          {tagline && (
            <div className={styles.similarRow}>
              <div className={styles.similarIcon}>
                <IconSingleStar />
              </div>
              <TextTiny>{tagline}</TextTiny>
            </div>
          )}

          {size === 'large' && (
            <div className={styles.fontCardImage}>
              <img src={fontFamily.previewUrl} alt={fontFamily.name} />
            </div>
          )}
          <div className={styles.fontCardFooter}>
            <div>
              <SegmentedFooter>
                <div className={styles.fontIconSourceContainer}>
                  <div className={styles.fontFamilySourceIcon}>
                    {getIconForFontSource(fontFamily.source)}
                  </div>
                  <TextTiny color="black">{fontFamily.name}</TextTiny>
                </div>
                {footer}
              </SegmentedFooter>
            </div>
            <Inline spacing={6}>
              {size === 'small' && previewButton}
              <div ref={likeCalloutRef}>
                <LikeButton
                  variant="secondary"
                  toggled={liked}
                  onClick={() => onLikeChange(!liked)}
                />
              </div>
            </Inline>
          </div>
        </Rows>
        {size === 'large' && (
          <div className={styles.floatingPreview}>{previewButton}</div>
        )}
        {isSelected && <div className={styles.fontCardSelected} />}
      </div>
      {showLikeCallout && (
        <Callout
          anchorRef={likeCalloutRef}
          title="Add to Liked Fonts to see similar suggestions"
          visible={likeCalloutVisible}
          onDismiss={dismissLikeCallout}
          delay={0}
          direction="vertical"
        />
      )}
    </>
  );
});

function FontCardSkeletons() {
  return (
    <Rows spacing={8}>
      {new Array(6).fill(null).map((_, i) => (
        <div
          className={styles.fontCardSkeleton}
          key={i}
          style={{
            animationDelay: `${i * 0.1 - 1}s`,
          }}
        />
      ))}
    </Rows>
  );
}

const FontCardSmallSkeletons = ({ count }: { count: number }) => (
  <Rows spacing={8}>
    {new Array(count).fill(null).map((_, i) => (
      <div
        className={styles.fontCardSmallSkeleton}
        key={i}
        style={{
          animationDelay: `${i * 0.1 - 1}s`,
        }}
      />
    ))}
  </Rows>
);

const FontCardAiSkeletons = ({ count = 6 }: { count?: number }) => (
  <Rows spacing={8}>
    {new Array(count).fill(null).map((_, i) => (
      <div
        className={styles.fontCardAiSkeleton}
        key={i}
        style={{
          animationDelay: `${i * 0.1 - 1}s`,
        }}
      >
        <Icon icon={IconStars} size={16} color="colorVeryLightPurple" />
      </div>
    ))}
  </Rows>
);

const FooterCard = observer(function _FooterCard({
  target,
  toastQueue,
  analytics,
}: {
  target: TypographyTarget;
  toastQueue: ToastQueue;
  analytics: AnalyticsService;
}) {
  const [fontFamily, setFontFamily] = useState<FontFamilyMetadata | null>(null);
  const conceptsStore = getGlobalConceptsStore();
  const concept = conceptsStore.activeConcept;
  const selectedFontFamily = concept.getFontFamily(target);

  useEffect(() => {
    const newFontFamily = conceptsStore.fontMetadataService.getFontFamily(
      selectedFontFamily.slug,
    );

    if (newFontFamily.value) {
      setFontFamily(newFontFamily.value);
    } else {
      setFontFamily(null);
      newFontFamily.promise.then((f) => {
        setFontFamily(f);
      });
    }
  }, [conceptsStore, selectedFontFamily]);

  const liked =
    fontFamily && concept.isFontFamilyLiked(target, fontFamily.slug);
  const onLikeChange = (liked: boolean) => {
    if (!fontFamily) return;
    setFontFamilyLiked(target, fontFamily, liked, conceptsStore, toastQueue);
  };

  const onShuffle = () => {
    concept.shuffle({
      typography: { heading: target === 'heading', body: target === 'body' },
    });
  };

  const onViewFont = () => {
    if (!fontFamily?.acquisitionUrl) {
      return;
    }

    const url = generateReferralUrl(fontFamily.acquisitionUrl);
    analytics.track({
      event: 'visit_font_information_button_clicked',
      link: url,
    });
    window.open(url, '_blank noopener noreferrer');
  };

  if (!fontFamily) {
    return null;
  }

  return (
    <Rows spacing={8}>
      <Button
        label="Shuffle"
        icon={<IconShuffleAi />}
        iconColor="ai"
        onClick={onShuffle}
      />
      <Inline spacing={4} xalign="center" yalign="center">
        <TextTinyLight color="grey" lineClamp={1}>
          Active font <Link onClick={onViewFont}>{fontFamily.name}</Link>
        </TextTinyLight>
        <LikeButton
          variant="subtle"
          toggled={!!liked}
          onClick={() => onLikeChange(!liked)}
        />
      </Inline>
    </Rows>
  );
});

const setFontFamilyLiked = (
  target: TypographyTarget,
  fontFamily: FontFamilyMetadata,
  liked: boolean,
  conceptsStore: ConceptsStore,
  toastQueue: ToastQueue,
) => {
  conceptsStore.activeConcept.setLikedFontFamilyState(
    target,
    fontFamily.slug,
    liked,
  );
  if (liked) {
    // TODO: remove the setTimeout once I figure out why the toast animation
    // stutters without it
    setTimeout(
      () =>
        toastQueue.push({
          label: 'Added to your liked',
          customLeft: (
            <div className={styles.likedToastIcon}>
              <IconThumbsUpFilled />
            </div>
          ),
        }),
      0,
    );
  }
};

type SearchState =
  | {
      state: 'success';
      result: FontFamilyPagedResponse;
    }
  | {
      state: 'error';
      err: string;
    }
  | {
      state: 'loading';
      cancel: () => void;
    };

function FontSearchResults({
  analytics,
  firstPageSearch,
  commitFontFamily,
  target,
  toastQueue,
}: {
  analytics: AnalyticsService;
  firstPageSearch: SearchState | null;
  commitFontFamily: CommitFontFamily;
  target: TypographyTarget;
  toastQueue: ToastQueue;
}) {
  const searchResultsLoader = useCallback(
    async (next: string | undefined) => {
      if (next === undefined) {
        // Initial call.
        if (firstPageSearch === null) {
          throw new Error(
            'Attempted to initalize InfiniteLoader with no search',
          );
        } else if (firstPageSearch.state === 'loading') {
          throw new Error(
            'Attempted to initalize InfiniteLoader before fetching first page.',
          );
        } else if (firstPageSearch.state === 'error') {
          throw new Error(
            'Attempted to initalize InfiniteLoader with errored response',
          );
        }
        return {
          continuation: firstPageSearch.result.next || undefined,
          items: firstPageSearch.result.results,
        };
      }

      const r =
        await getGlobalConceptsStore().fontMetadataService.getSearchFontFamiliesPage(
          next,
        );
      return {
        continuation: r.next || undefined,
        items: r.results,
      };
    },
    [firstPageSearch],
  );
  // The only time firstPageSearch should be null but we render search results
  // is in the brief period between a query being inputted and useEffect firing.
  // We treat this state as "loading".
  if (firstPageSearch === null || firstPageSearch.state === 'loading') {
    return <FontCardSkeletons />;
  }
  if (firstPageSearch.state === 'error') {
    return <p>Something went wrong: {firstPageSearch.err}</p>;
  }

  function renderSearchResults(items: readonly FontFamilyMetadata[]) {
    return items.map((f) => (
      <FontFamilyCard
        analytics={analytics}
        commitFontFamily={commitFontFamily}
        category="search"
        key={f.slug}
        fontFamily={f}
        target={target}
        toastQueue={toastQueue}
      />
    ));
  }
  function LoadingState() {
    return <p> Loading ...</p>;
  }
  function ErrorState(err: unknown) {
    return <p> Error: {(err as Error).message || String(err)}</p>;
  }
  return (
    <Rows spacing={8}>
      <InfiniteScroll
        loader={searchResultsLoader}
        render={renderSearchResults}
        LoadingState={LoadingState}
        ErrorState={ErrorState}
      />
    </Rows>
  );
}

const FontSearch = observer(function _FontSearch({
  analytics,
  commitFontFamily,
  target,
  navigation,
  hidden,
  toastQueue,
  enableMonotypeFonts,
  setHeader,
}: {
  analytics: AnalyticsService;
  commitFontFamily: CommitFontFamily;
  target: TypographyTarget;
  navigation: SidebarNavigation;
  hidden?: boolean;
  toastQueue: ToastQueue;
  enableMonotypeFonts: boolean;
  setHeader: ((header: React.ReactNode) => void) | null;
}) {
  const [firstPageSearch, setFirstPageSearch] = useState<SearchState | null>(
    null,
  );

  const at =
    navigation.at?.path === 'fontFamily/search' ? navigation.at : undefined;

  const [query, setQuery] = useState<string>(at?.query ?? '');
  const globalTypographyPanelStore = getGlobalTypographyPanelStore();
  const includePremiumFonts =
    enableMonotypeFonts && globalTypographyPanelStore.includePremiumFonts;

  const ref = React.useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (hidden) {
      setQuery('');
    } else {
      setQuery(at?.query ?? '');
      ref.current?.focus();
    }
  }, [at?.query, hidden]);

  const reloadResults = useStableCallback(() => {
    if (firstPageSearch?.state === 'loading') {
      firstPageSearch.cancel();
    }
    const controller = new AbortController();
    analytics.track({
      event: 'project:font_family_search_query_updated',
      target,
      query,
    });
    getGlobalConceptsStore()
      .fontMetadataService.searchFontFamilies(
        query,
        [],
        controller.signal,
        includePremiumFonts,
      )
      .then((result) => {
        setFirstPageSearch({ state: 'success', result });
      })
      .catch((reason: unknown) => {
        const err = reason as Error;
        if (err.name === 'AbortError') {
          // This request was cancelled.
          return;
        }
        setFirstPageSearch({ state: 'error', err: String(err) });
      });
    setFirstPageSearch({
      state: 'loading',
      cancel: () => controller.abort(),
    });
  });

  useOnMount(() => {
    reloadResults();
  });

  useDebouncyEffect(
    () => {
      reloadResults();
    },
    200,
    [query, includePremiumFonts],
  );

  let header;
  if (firstPageSearch?.state === 'success') {
    const { count } = firstPageSearch.result;
    header = `${count} ${pluralise(count, 'result')}`;
  } else {
    header = '';
  }

  useEffect(() => {
    setHeader?.(
      // The structure of the DOM tree that holds the `FormInput` **must** be equal
      // to the structure use in the `setHeader` of the `dashboard` panel.
      // Otherwise, when the user starts typing the focus will be lost
      // (because React will unmount the `FormInput`).
      <>
        <Rows>
          <FormInput
            iconStart={IconSearch}
            value={query}
            onChange={setQuery}
            placeholder="Search"
            type="search"
            ref={ref}
            buttonEnd={
              <Button
                size="small"
                variant="subtle"
                icon={IconClear}
                onClick={() =>
                  navigation.goto({
                    path: 'fontFamily',
                    target,
                    tab: at?.tab,
                  })
                }
              />
            }
          />
          {enableMonotypeFonts && <FreeFontFilterToggle target={target} />}
        </Rows>
      </>,
    );
  }, [at?.tab, navigation, query, setHeader, target, enableMonotypeFonts]);

  return (
    <Rows>
      <Inline yalign="center" xalign="space-between">
        <TextTiny color="grey">{header}</TextTiny>
      </Inline>
      <FontSearchResults
        analytics={analytics}
        firstPageSearch={firstPageSearch}
        commitFontFamily={commitFontFamily}
        target={target}
        toastQueue={toastQueue}
      />
    </Rows>
  );
});

const FontCategory = observer(function _FontCategory({
  analytics,
  target,
  category,
  commitFontFamily,
  toastQueue,
  enableMonotypeFonts,
  showHeader = true,
}: {
  analytics: AnalyticsService;
  target: TypographyTarget;
  category: TypographyCategory | undefined;
  commitFontFamily: CommitFontFamily;
  toastQueue: ToastQueue;
  enableMonotypeFonts: boolean;
  showHeader?: boolean;
}) {
  const [firstPageSearch, setFirstPageSearch] = useState<SearchState | null>(
    null,
  );
  const globalTypographyPanelStore = getGlobalTypographyPanelStore();
  const includePremiumFonts =
    enableMonotypeFonts && globalTypographyPanelStore.includePremiumFonts;

  const reloadResults = useStableCallback(() => {
    if (firstPageSearch?.state === 'loading') {
      firstPageSearch.cancel();
    }
    const controller = new AbortController();
    getGlobalConceptsStore()
      .fontMetadataService.searchFontFamilies(
        '',
        category ? [category] : [],
        controller.signal,
        includePremiumFonts,
      )
      .then((result) => {
        setFirstPageSearch({ state: 'success', result });
      })
      .catch((reason: unknown) => {
        const err = reason as Error;
        if (err.name === 'AbortError') {
          // This request was cancelled.
          return;
        }
        setFirstPageSearch({ state: 'error', err: String(err) });
      });
    setFirstPageSearch({
      state: 'loading',
      cancel: () => controller.abort(),
    });
  });

  useEffect(reloadResults, [includePremiumFonts, reloadResults]);

  useOnMount(() => {
    reloadResults();
  });

  let header;
  if (firstPageSearch?.state === 'success') {
    const { count } = firstPageSearch.result;
    header = `${count} ${pluralise(count, 'result')}`;
  } else {
    // Prevent header from collapsing when results are loading
    header = <>&nbsp;</>;
  }

  return (
    <Rows>
      {showHeader && (
        <Inline yalign="center" xalign="space-between">
          <TextTiny color="grey">{header}</TextTiny>
        </Inline>
      )}
      <FontSearchResults
        analytics={analytics}
        firstPageSearch={firstPageSearch}
        commitFontFamily={commitFontFamily}
        target={target}
        toastQueue={toastQueue}
      />
    </Rows>
  );
});

const LikedFontsEmptyState = () => (
  <>
    <div className={styles.likedFontsEmptyState}>
      <div className={styles.iconContainer}>
        <Icon icon={IconThumbsUpFilled} size={16} color="colorPurple" />
      </div>
      <TextSmallLight align="center" color="grey">
        Like fonts to unlock personalized recommendations
      </TextSmallLight>
    </div>
  </>
);

const LikedFonts = observer(function _LikedFonts({
  analytics,
  commitFontFamily,
  target,
  toastQueue,
}: {
  analytics: AnalyticsService;
  commitFontFamily: CommitFontFamily;
  target: TypographyTarget;
  toastQueue: ToastQueue;
}) {
  const [likedFonts, setLikedFonts] = useState<FontFamilyMetadata[] | null>(
    null,
  );
  const [likesExpanded, setLikesExpanded] = useState<boolean>(false);
  const conceptsStore = getGlobalConceptsStore();
  const concept = conceptsStore.activeConcept;
  const likedFontFamilySlugs = concept.getLikedFontFamilies(target);
  useEffect(() => {
    const requests = likedFontFamilySlugs.map(
      (slug) => conceptsStore.fontMetadataService.getFontFamily(slug).promise,
    );
    Promise.all(requests).then(setLikedFonts);
  }, [conceptsStore, likedFontFamilySlugs]);

  let renderedLikedFonts;
  if (likedFonts === null) {
    renderedLikedFonts = (
      <FontCardSmallSkeletons count={likedFontFamilySlugs.length} />
    );
  } else if (likedFonts.length > 4 && !likesExpanded) {
    renderedLikedFonts = (
      <Rows spacing={8}>
        {likedFonts.slice(0, 4).map((f) => (
          <FontFamilyCard
            analytics={analytics}
            category="liked"
            commitFontFamily={commitFontFamily}
            key={f.slug}
            fontFamily={f}
            size="small"
            target={target}
            toastQueue={toastQueue}
          />
        ))}
        <Button
          onClick={() => setLikesExpanded(true)}
          label="View all"
          variant="secondary"
        />
      </Rows>
    );
  } else {
    renderedLikedFonts = (
      <Rows spacing={8}>
        {likedFonts.map((f) => (
          <FontFamilyCard
            analytics={analytics}
            category="liked"
            commitFontFamily={commitFontFamily}
            key={f.slug}
            fontFamily={f}
            size="small"
            target={target}
            toastQueue={toastQueue}
          />
        ))}
      </Rows>
    );
  }
  return (
    <Rows>
      <TextSmall>Liked</TextSmall>
      {renderedLikedFonts}
    </Rows>
  );
});

const RecommendedFonts = observer(function RecommendedFonts({
  analytics,
  commitFontFamily,
  target,
  toastQueue,
}: {
  analytics: AnalyticsService;
  commitFontFamily: CommitFontFamily;
  target: TypographyTarget;
  toastQueue: ToastQueue;
}) {
  const globalTypographyPanelStore = getGlobalTypographyPanelStore();

  const cachedRecommendedFonts =
    globalTypographyPanelStore.getRecommendedFontFamilies(target);

  // State local to the panel; will be cleared when it's closed.
  // Stores extra results loaded when the user clicks "Show more", which we don't want to persist.
  const [localRecommendedFonts, setLocalRecommendedFonts] = useState<
    typeof cachedRecommendedFonts | null
  >(null);

  // Clear localRecommendedFonts when cachedRecommendedFonts changes (e.g. a new font is liked)
  useEffect(() => setLocalRecommendedFonts(null), [cachedRecommendedFonts]);

  const recommendedFonts = localRecommendedFonts ?? cachedRecommendedFonts;

  useOnMount(() => {
    if (!recommendedFonts) {
      void globalTypographyPanelStore.reloadRecommendedFontFamilies(target);
    }
  });

  const [loadingMore, setLoadingMore] = useState(false);

  const canLoadMore =
    recommendedFonts &&
    recommendedFonts.length % 10 === 0 &&
    recommendedFonts.length + 10 <= 50;

  const handleLoadMore = useStableCallback(() => {
    if (recommendedFonts && canLoadMore) {
      setLoadingMore(true);
      globalTypographyPanelStore
        .loadRecommendedFontFamilies(target, recommendedFonts.length + 10)
        .then((results) => setLocalRecommendedFonts(results))
        .finally(() => {
          setLoadingMore(false);
        });
    }
  });

  let cards: React.ReactNode;
  if (recommendedFonts === null) {
    cards = <FontCardAiSkeletons />;
  } else {
    cards = (
      <Rows spacing={8}>
        {recommendedFonts.map(({ fontFamily: f, similarTo }) => (
          <FontFamilyCard
            analytics={analytics}
            category="recommended"
            commitFontFamily={commitFontFamily}
            key={f.slug}
            fontFamily={f}
            tagline={`Similar to ${similarTo}`}
            target={target}
            toastQueue={toastQueue}
          />
        ))}
        {loadingMore && <FontCardAiSkeletons count={10} />}
      </Rows>
    );
  }
  return (
    <Rows spacing={12}>
      {cards}
      {canLoadMore && <Button onClick={handleLoadMore} label="Show more" />}
    </Rows>
  );
});

const PopularFonts = observer(function _PopularFonts({
  analytics,
  commitFontFamily,
  target,
  toastQueue,
}: {
  analytics: AnalyticsService;
  commitFontFamily: CommitFontFamily;
  target: TypographyTarget;
  toastQueue: ToastQueue;
}) {
  const globalTypographyPanelStore = getGlobalTypographyPanelStore();

  const popularFonts =
    globalTypographyPanelStore.getPopularFontFamilies(target);

  useOnMount(() => {
    if (!popularFonts) {
      void globalTypographyPanelStore.reloadPopularFontFamilies(target);
    }
  });

  const handleReload = useStableCallback(() => {
    void globalTypographyPanelStore.reloadPopularFontFamilies(target);
    analytics.track({
      event: 'project:reload_popular_font_families_button_click',
      target,
    });
  });

  let cards: React.ReactNode;
  if (popularFonts === null) {
    cards = <FontCardAiSkeletons />;
  } else {
    cards = (
      <Rows spacing={8}>
        {popularFonts.map(({ fontFamily: f }) => (
          <FontFamilyCard
            category="popular"
            analytics={analytics}
            commitFontFamily={commitFontFamily}
            key={f.slug}
            fontFamily={f}
            target={target}
            tagline="Like for similar"
            toastQueue={toastQueue}
          />
        ))}
      </Rows>
    );
  }
  return (
    <Rows spacing={12}>
      {cards}
      <Button
        iconColor="ai"
        onClick={handleReload}
        icon={IconReplaceAi}
        label="Refresh list"
      />
    </Rows>
  );
});

const HeadingCollectionsSection = observer(function _HeadingCollectionSection({
  navigation,
}: {
  navigation: SidebarNavigation;
}) {
  const tab =
    navigation.at?.path === 'fontFamily' ? navigation.at.tab : undefined;

  return (
    <Rows spacing={12}>
      <Rows spacing={4}>
        <TextSmall>Featured collections</TextSmall>
        <TextTinyLight color="grey">Handpicked for you</TextTinyLight>
      </Rows>
      <Rows spacing={8}>
        {HEADING_FONT_COLLECTION_SLUGS.map((slug) => (
          <HeadingCollectionsCard
            key={slug}
            slug={slug}
            onClick={() =>
              navigation.goto({
                path: 'fontFamily/collection',
                target: 'heading',
                tab,
                slug,
              })
            }
          />
        ))}
      </Rows>
    </Rows>
  );
});

const HeadingCollectionsCard = ({
  slug,
  onClick,
}: {
  slug: HeadingFontCollectionSlug;
  onClick?: () => void;
}) => {
  const { name, image, isNew } = HEADING_FONT_COLLECTIONS[slug];
  return (
    <div className={styles.collectionCard} onClick={onClick}>
      <div className={styles.collectionCardImageContainer}>
        <img src={image} />
        <div className={styles.collectionCardHover} />
      </div>
      <div className={styles.collectionCardFooter}>
        <TextTiny>{name}</TextTiny>
        {isNew && <Badge label="New" variant="green" />}
      </div>
    </div>
  );
};

const HeadingCollectionsSectionSmall = observer(
  function _HeadingCollectionSectionSmall({
    navigation,
  }: {
    navigation: SidebarNavigation;
  }) {
    const tab =
      navigation.at?.path === 'fontFamily' ? navigation.at.tab : undefined;

    return (
      <>
        <TextSmall>Collections</TextSmall>
        <div className={styles.collectionCardSmallContainer}>
          {Object.entries(HEADING_FONT_COLLECTIONS).map(([slug, { image }]) => {
            return (
              <div
                key={slug}
                className={styles.collectionCardSmall}
                onClick={() => {
                  navigation.goto({
                    path: 'fontFamily/collection',
                    target: 'heading',
                    tab,
                    slug: slug as HeadingFontCollectionSlug,
                  });
                }}
              >
                <img src={image} />
                <div className={styles.collectionCardSmallHover} />
              </div>
            );
          })}
        </div>
      </>
    );
  },
);

const CollectionPage = observer(function _CollectionPage({
  analytics,
  commitFontFamily,
  target,
  slug,
  toastQueue,
}: {
  analytics: AnalyticsService;
  commitFontFamily: CommitFontFamily;
  target: TypographyTarget;
  slug: HeadingFontCollectionSlug;
  toastQueue: ToastQueue;
}) {
  const [collection, setCollection] = useState<FontCollection | null>(null);
  useEffect(() => {
    getGlobalConceptsStore()
      .fontMetadataService.getFontCollection(slug)
      .then(setCollection)
      .catch((err) => {
        console.error(err);
      });
  }, [slug]);

  if (collection === null) {
    return <FontCardSkeletons />;
  }

  const metadata = HEADING_FONT_COLLECTIONS[slug];

  return (
    <Rows>
      <ExpandToEdges>
        <img src={metadata.image} className={styles.collectionPageImage} />
      </ExpandToEdges>
      <Rows spacing={6}>
        <TextRegular>{metadata.descriptionHeading}</TextRegular>
        <TextSmallLight color="grey">{metadata.description}</TextSmallLight>
      </Rows>
      <Rows spacing={8}>
        {collection.fontFamilies.map((f) => (
          <FontFamilyCard
            analytics={analytics}
            category="collection"
            collection={slug}
            commitFontFamily={commitFontFamily}
            key={f.slug}
            fontFamily={f}
            target={target}
            toastQueue={toastQueue}
          />
        ))}
      </Rows>
    </Rows>
  );
});

const FontDashboard = observer(function _FontDashboard({
  analytics,
  commitFontFamily,
  target,
  navigation,
  toastQueue,
  enableMonotypeFonts,
  setHeader,
  tab,
}: {
  analytics: AnalyticsService;
  commitFontFamily: CommitFontFamily;
  target: TypographyTarget;
  navigation: SidebarNavigation;
  toastQueue: ToastQueue;
  enableMonotypeFonts: boolean;
  setHeader: ((header: React.ReactNode) => void) | null;
  tab: 'liked' | 'browse';
}) {
  const conceptsStore = getGlobalConceptsStore();
  const concept = conceptsStore.activeConcept;
  const likedFontFamilies = concept.getLikedFontFamilies(target);

  useEffect(() => {
    setHeader?.(
      // The structure of the DOM tree that holds the `FormInput` **must** be equal
      // to the structure use in the `setHeader` of the `search` panel.
      // Otherwise, when the user starts typing the focus will be lost
      // (because React will unmount the `FormInput`).
      <>
        <Rows>
          <FormInput
            iconStart={IconSearch}
            value=""
            onChange={(v) =>
              navigation.goto({
                path: 'fontFamily/search',
                target,
                query: v,
                tab,
              })
            }
            placeholder="Search"
            type="search"
          />
        </Rows>
        <Rows spacing={0}>
          <ExpandToEdges>
            <TabList
              options={
                [
                  { value: 'liked', label: 'Liked Fonts' },
                  { value: 'browse', label: 'Browse' },
                ] as const
              }
              selectedValue={tab ?? 'liked'}
              onChange={(v) =>
                navigation.goto({ path: 'fontFamily', target, tab: v })
              }
            />
          </ExpandToEdges>
        </Rows>
      </>,
    );
  }, [navigation, setHeader, tab, target]);

  if (tab === 'browse') {
    return (
      <Rows spacing={24}>
        <CategoryButtons target={target} navigation={navigation} />
        {target === 'heading' && (
          <HeadingCollectionsSection navigation={navigation} />
        )}
        {target === 'body' && (
          <Rows spacing={12}>
            <TextSmall>Popular fonts</TextSmall>
            <FontCategory
              analytics={analytics}
              target={target}
              enableMonotypeFonts={enableMonotypeFonts}
              category={undefined}
              commitFontFamily={commitFontFamily}
              toastQueue={toastQueue}
              showHeader={false}
            />
          </Rows>
        )}
      </Rows>
    );
  }

  return (
    <Rows>
      {likedFontFamilies.length > 0 ? (
        <LikedFonts
          analytics={analytics}
          commitFontFamily={commitFontFamily}
          target={target}
          toastQueue={toastQueue}
        />
      ) : (
        <LikedFontsEmptyState />
      )}
      <DividerLine />
      <Rows spacing={2}>
        <TextSmall>My Recommendations</TextSmall>
        <TextTinyLight color="grey">
          AI suggestions based on your likes
        </TextTinyLight>
      </Rows>
      {enableMonotypeFonts && <FreeFontFilterToggle target={target} />}
      {likedFontFamilies.length > 0 ? (
        <RecommendedFonts
          analytics={analytics}
          commitFontFamily={commitFontFamily}
          target={target}
          toastQueue={toastQueue}
        />
      ) : (
        <PopularFonts
          analytics={analytics}
          commitFontFamily={commitFontFamily}
          target={target}
          toastQueue={toastQueue}
        />
      )}
      {target === 'heading' && (
        <>
          <DividerLine />
          <HeadingCollectionsSectionSmall navigation={navigation} />
        </>
      )}
    </Rows>
  );
});

export const TypographyEditorPanel = observer(
  ({
    analytics,
    navigation,
    toastQueue,
    session,
  }: TypographyEditorPanelProps) => {
    const target =
      navigation.at && 'target' in navigation.at
        ? navigation.at.target
        : undefined;
    if (!target) {
      throw new Error('No target provided');
    }

    const conceptsStore = getGlobalConceptsStore();
    const concept = conceptsStore.activeConcept;
    const commitFontFamily = useCallback(
      (fontFamily: FontFamilyMetadata) => {
        concept.setFontFamily(target, fontFamily);
      },
      [concept, target],
    );

    const at = navigation.at!;

    const [header, setHeader] = useState<React.ReactNode | null>(null);
    let title: string;

    switch (at.path) {
      case 'fontFamily/collection':
        title = 'Collection';
        break;
      case 'fontFamily/category':
        title =
          TYPOGRAPHY_CATEGORIES.find(({ slug }) => slug === at.category)
            ?.label ?? '';
        break;
      case 'fontFamily':
      case 'fontFamily/search':
        title = target === 'heading' ? 'Heading' : 'Body';
        break;
      default:
        return null;
    }
    const enableMonotypeFonts = session.flags.get('enableMonotypeFonts');
    useEffect(() => {
      if (at.path === 'fontFamily/category' && enableMonotypeFonts) {
        setHeader(<FreeFontFilterToggle target={target} />);
        return;
      }
      if (at.path === 'fontFamily/collection') {
        setHeader(undefined);
      }
    }, [at.path, target, enableMonotypeFonts]);

    const hideHeaderDivider = at.path === 'fontFamily';

    return (
      <PanelLayout
        title={title}
        navigation={navigation}
        header={header}
        hideHeaderDivider={hideHeaderDivider}
        footer={
          <FooterCard
            target={target}
            toastQueue={toastQueue}
            analytics={analytics}
          />
        }
        disableWrapChildren
      >
        {at.path === 'fontFamily/collection' && (
          <PanelScrollable hideTopPadding={true}>
            <CollectionPage
              analytics={analytics}
              commitFontFamily={commitFontFamily}
              target={target}
              slug={at.slug}
              toastQueue={toastQueue}
            />
          </PanelScrollable>
        )}
        {at.path === 'fontFamily/category' && (
          <PanelScrollable>
            <FontCategory
              enableMonotypeFonts={session.flags.get('enableMonotypeFonts')}
              analytics={analytics}
              commitFontFamily={commitFontFamily}
              target={target}
              toastQueue={toastQueue}
              category={at.category}
            />
          </PanelScrollable>
        )}
        <PanelScrollable hide={at.path !== 'fontFamily/search'}>
          <FontSearch
            analytics={analytics}
            commitFontFamily={commitFontFamily}
            enableMonotypeFonts={session.flags.get('enableMonotypeFonts')}
            target={target}
            navigation={navigation}
            hidden={at.path !== 'fontFamily/search'}
            toastQueue={toastQueue}
            setHeader={at.path === 'fontFamily/search' ? setHeader : null}
          />
        </PanelScrollable>
        <PanelScrollable hide={at.path !== 'fontFamily'}>
          <FontDashboard
            analytics={analytics}
            commitFontFamily={commitFontFamily}
            target={target}
            enableMonotypeFonts={enableMonotypeFonts}
            navigation={navigation}
            toastQueue={toastQueue}
            setHeader={at.path === 'fontFamily' ? setHeader : null}
            tab={(at.path === 'fontFamily' && at.tab) || 'liked'}
          />
        </PanelScrollable>
      </PanelLayout>
    );
  },
);

export const TYPOGRAPHY_CATEGORIES = [
  {
    slug: 'sans-serif',
    label: 'Sans Serif',
    icon: IconSansSerif,
    image: typeSansSerifImage,
  },
  { slug: 'serif', label: 'Serif', icon: IconSerif, image: typeSerifImage },
  {
    slug: 'display',
    label: 'Display',
    icon: IconDisplay,
    image: typeDisplayImage,
  },
  {
    slug: 'monospace',
    label: 'Monospace',
    icon: IconMono,
    image: typeMonospaceImage,
  },
] as const;

const CategoryButtons = ({
  target,
  navigation,
}: {
  target: TypographyTarget;
  navigation: SidebarNavigation;
}) => {
  const tab =
    navigation.at?.path === 'fontFamily' ? navigation.at.tab : undefined;

  return (
    <div className={styles.categoryButtons}>
      {TYPOGRAPHY_CATEGORIES.map(({ slug, label, icon }) => (
        <Button
          variant="secondary"
          key={slug}
          onClick={() =>
            navigation.goto({
              path: 'fontFamily/category',
              target,
              tab,
              category: slug,
            })
          }
        >
          <div className={styles.categoryButton}>
            <Icon icon={icon} size={24} />
            <TextTiny>{label}</TextTiny>
          </div>
        </Button>
      ))}
    </div>
  );
};

type FontCacheKey = `${TypographyTarget}-${boolean}`;

const LOCAL_STORAGE_PREMIUM_FONT_FILTER_KEY =
  'LOCAL_STORAGE_PREMIUM_FONT_FILTER_KEY';
class TypographyPanelStore {
  private recommendedFontFamiliesCache = observable.map<
    FontCacheKey,
    ListRecommendedFontFamiliesResponse
  >();

  private popularFontFamiliesCache = observable.map<
    FontCacheKey,
    ListFontFamiliesFromCollectionsResponse
  >();

  private _includePremiumFonts: boolean = true;

  constructor() {
    makeAutoObservable(this);
    const conceptsStore = getGlobalConceptsStore();
    reaction(
      () => conceptsStore.activeConcept,
      () => {
        this.reset();
      },
    );

    for (const target of ['heading', 'body'] as const) {
      reaction(
        () => conceptsStore.activeConcept.state.fontFamilies[target].liked,
        () => {
          this.reloadRecommendedFontFamilies(target);
        },
      );
    }
    this._includePremiumFonts = this.getIncludingPremiumFontFromStorage();
    listenToStorageEvent(() => {
      this._includePremiumFonts = this.getIncludingPremiumFontFromStorage();
    });
  }

  get includePremiumFonts() {
    return this._includePremiumFonts;
  }

  private getIncludingPremiumFontFromStorage() {
    return (
      (getLocalStorageItem(LOCAL_STORAGE_PREMIUM_FONT_FILTER_KEY) || 'true') ===
      'true'
    );
  }

  @action
  toggleFilteringPremiumFonts() {
    this._includePremiumFonts = !this._includePremiumFonts;
    localStorage.setItem(
      LOCAL_STORAGE_PREMIUM_FONT_FILTER_KEY,
      this._includePremiumFonts.toString(),
    );
  }

  private getCacheKey(target: TypographyTarget): FontCacheKey {
    return `${target}-${this._includePremiumFonts}`;
  }

  private reset() {
    this.recommendedFontFamiliesCache.clear();
    this.popularFontFamiliesCache.clear();
  }

  getRecommendedFontFamilies(target: TypographyTarget) {
    return (
      this.recommendedFontFamiliesCache.get(this.getCacheKey(target)) ?? null
    );
  }

  getPopularFontFamilies(target: TypographyTarget) {
    return this.popularFontFamiliesCache.get(this.getCacheKey(target)) ?? null;
  }

  async loadRecommendedFontFamilies(target: TypographyTarget, count: number) {
    const conceptsStore = getGlobalConceptsStore();
    const activeConcept = conceptsStore.activeConcept;

    return conceptsStore.fontMetadataService.listRecommendedFontFamilies(
      activeConcept.state.fontFamilies[target].liked,
      count,
      this._includePremiumFonts,
    );
  }

  async reloadRecommendedFontFamilies(target: TypographyTarget) {
    const conceptsStore = getGlobalConceptsStore();
    const activeConcept = conceptsStore.activeConcept;
    this.recommendedFontFamiliesCache.delete(this.getCacheKey(target));
    if (!activeConcept.state.fontFamilies[target].liked.length) {
      return [];
    }

    const result =
      await conceptsStore.fontMetadataService.listRecommendedFontFamilies(
        activeConcept.state.fontFamilies[target].liked,
        10,
        this._includePremiumFonts,
      );

    this.recommendedFontFamiliesCache.set(this.getCacheKey(target), result);

    return result;
  }

  async reloadPopularFontFamilies(target: TypographyTarget) {
    const conceptsStore = getGlobalConceptsStore();

    const collections =
      target === 'heading'
        ? HEADING_FONT_COLLECTION_SLUGS
        : BODY_FONT_COLLECTION_SLUGS;

    const result =
      await conceptsStore.fontMetadataService.listFontFamiliesFromCollections(
        collections,
      );
    this.popularFontFamiliesCache.set(this.getCacheKey(target), result);

    return result;
  }
}

let _globalTypographyPanelStore: TypographyPanelStore | undefined;
export const getGlobalTypographyPanelStore = () => {
  if (!_globalTypographyPanelStore) {
    _globalTypographyPanelStore = new TypographyPanelStore();
  }
  return _globalTypographyPanelStore;
};
